import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Square, AlertTriangle, TrendingUp, DollarSign, Activity, Settings, LogOut, User, Zap } from 'lucide-react';
import ApiKeyModal from './ApiKeyModal';
import TradingChart from './TradingChart';
import AdvancedTradingChart from './AdvancedTradingChart';
import QuickTradeStatus from './QuickTradeStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// USD to INR conversion rate (approximate)
const USD_TO_INR = 83.5;

// Helper function to convert and format INR
const toINR = (usdValue) => {
  if (!usdValue) return '0.00';
  const inrValue = usdValue * USD_TO_INR;
  return inrValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const TradingDashboard = ({ user, onLogout }) => {
  const [botStatus, setBotStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [ticker, setTicker] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [hasApiKeys, setHasApiKeys] = useState(false);
  const [tradingMode, setTradingMode] = useState('paper'); // 'paper' or 'live'
  const [isFutures, setIsFutures] = useState(true); // Default to futures

  useEffect(() => {
    fetchData();
    checkApiKeys();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const checkApiKeys = () => {
    const keys = localStorage.getItem('kucoin_api_keys');
    setHasApiKeys(!!keys);
    
    // Check saved trading mode
    const savedMode = localStorage.getItem('trading_mode');
    if (savedMode) {
      setTradingMode(savedMode);
    }
  };

  const handleToggleTradingMode = () => {
    if (!hasApiKeys && tradingMode === 'paper') {
      alert('Please add KuCoin Futures API keys first before switching to Live Trading!');
      return;
    }
    
    const newMode = tradingMode === 'paper' ? 'live' : 'paper';
    
    if (newMode === 'live') {
      const confirm = window.confirm(
        '⚠️ WARNING: You are switching to LIVE TRADING mode!\n\n' +
        'This will use REAL MONEY on KuCoin.\n' +
        'Make sure you have:\n' +
        '• Added valid KuCoin API keys\n' +
        '• Tested in paper trading first\n' +
        '• Small capital to start\n\n' +
        'Are you sure you want to proceed?'
      );
      
      if (!confirm) return;
    }
    
    setTradingMode(newMode);
    localStorage.setItem('trading_mode', newMode);
    
    // Restart bot with new mode
    if (isRunning) {
      handleStopBot();
      setTimeout(() => {
        handleStartBot();
      }, 2000);
    }
  };

  const fetchData = async () => {
    try {
      const [statusRes, tradesRes, perfRes, tickerRes, confidenceRes] = await Promise.all([
        axios.get(`${API}/bot/status`),
        axios.get(`${API}/trades?limit=10`),
        axios.get(`${API}/performance`),
        axios.get(`${API}/market/ticker`),
        axios.get(`${API}/market/confidence`)
      ]);
      
      setBotStatus(statusRes.data);
      setTrades(tradesRes.data);
      setPerformance(perfRes.data);
      setTicker(tickerRes.data);
      setConfidence(confidenceRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleStartBot = async () => {
    setLoading(true);
    try {
      const isPaperTrading = tradingMode === 'paper';
      
      // Prepare request body - Default to FUTURES trading
      const requestBody = { 
        is_paper_trading: isPaperTrading,
        is_futures: isFutures  // Always futures by default
      };
      
      // Add API keys for live trading
      if (!isPaperTrading && hasApiKeys) {
        const savedKeys = localStorage.getItem('kucoin_api_keys');
        if (savedKeys) {
          const keys = JSON.parse(savedKeys);
          requestBody.api_key = keys.kucoin_api_key;
          requestBody.api_secret = keys.kucoin_api_secret;
          requestBody.api_passphrase = keys.kucoin_api_passphrase;
        }
      }
      
      await axios.post(`${API}/bot/start`, requestBody);
      fetchData();
    } catch (error) {
      console.error('Error starting bot:', error);
      alert('Failed to start bot: ' + error.message);
    }
    setLoading(false);
  };

  const handleStopBot = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/bot/stop`);
      fetchData();
    } catch (error) {
      console.error('Error stopping bot:', error);
    }
    setLoading(false);
  };

  const handleEmergencyStop = async () => {
    if (window.confirm('Are you sure you want to trigger EMERGENCY STOP?')) {
      setLoading(true);
      try {
        await axios.post(`${API}/bot/emergency-stop`);
        fetchData();
      } catch (error) {
        console.error('Error:', error);
      }
      setLoading(false);
    }
  };

  const isRunning = botStatus?.bot_status?.is_running || false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white">
      <div className="border-b border-purple-800 bg-gray-900/50 backdrop-blur">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-purple-400">
                <Zap className="inline mr-2" size={28} />
                AI Futures Trading Bot
              </h1>
              <p className="text-sm text-gray-400 mt-1">ETH/USDT Perpetual • 5x Leverage • Ultra-Safe Mode</p>
              <div className="flex items-center gap-3 mt-2">
                {/* Futures Badge */}
                <div className="flex items-center gap-2 px-3 py-1 border rounded-lg bg-purple-900/50 border-purple-700">
                  <span className="text-xs font-semibold text-purple-300">
                    ⚡ ETH LONG-ONLY
                  </span>
                  <span className="text-xs text-gray-400">
                    (5x Leverage • Confidence Scoring)
                  </span>
                </div>
                
                {/* Paper/Live Trading Badge */}
                <div className={`flex items-center gap-2 px-3 py-1 border rounded-lg ${
                  tradingMode === 'paper' 
                    ? 'bg-blue-900/50 border-blue-700' 
                    : 'bg-red-900/50 border-red-700'
                }`}>
                  <span className={`text-xs font-semibold ${
                    tradingMode === 'paper' ? 'text-blue-300' : 'text-red-300'
                  }`}>
                    {tradingMode === 'paper' ? '📝 PAPER TRADING' : '🔴 LIVE TRADING'}
                  </span>
                </div>
                
                {/* Trading Mode Toggle Button */}
                <button
                  onClick={handleToggleTradingMode}
                  className={`px-4 py-1 rounded-lg text-xs font-semibold transition ${
                    tradingMode === 'paper'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  }`}
                >
                  {tradingMode === 'paper' ? '🚀 Go Live' : '🛡️ Paper Mode'}
                </button>
                
                {!hasApiKeys && (
                  <p className="text-xs text-yellow-400">⚠️ Add Futures API Keys</p>
                )}
                {hasApiKeys && (
                  <p className="text-xs text-green-400">✅ KuCoin Connected</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* User Info */}
              {user && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-700/50 rounded-lg border border-gray-600">
                  <User size={18} className="text-gray-400" />
                  <span className="text-sm text-gray-300 hidden md:inline">{user.email}</span>
                </div>
              )}

              {/* API Keys Button */}
              <button
                onClick={() => setShowApiKeyModal(true)}
                className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 px-4 py-3 rounded-lg transition"
                title="API Settings"
              >
                <Settings size={20} />
                <span className="hidden md:inline">API Keys</span>
              </button>

              {/* Logout Button */}
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-4 py-3 rounded-lg transition"
                  title="Logout"
                >
                  <LogOut size={20} />
                  <span className="hidden md:inline">Logout</span>
                </button>
              )}
            </div>
          </div>

          {/* Trading Control Buttons */}
          <div className="flex gap-3 mt-4">
            {!isRunning ? (
              <button
                onClick={handleStartBot}
                disabled={loading}
                data-testid="start-bot-btn"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-semibold transition disabled:opacity-50"
              >
                <Play size={20} />
                Start Bot
              </button>
            ) : (
              <button
                onClick={handleStopBot}
                disabled={loading}
                data-testid="stop-bot-btn"
                className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 px-6 py-3 rounded-lg font-semibold transition disabled:opacity-50"
              >
                <Square size={20} />
                Stop Bot
              </button>
            )}
            
            <button
              onClick={handleEmergencyStop}
              disabled={loading || !isRunning}
              data-testid="emergency-stop-btn"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-lg font-semibold transition disabled:opacity-30"
            >
              <AlertTriangle size={20} />
              Emergency Stop
            </button>
          </div>
        </div>
      </div>

      {showApiKeyModal && (
        <ApiKeyModal 
          onClose={() => setShowApiKeyModal(false)} 
          onSave={() => checkApiKeys()}
        />
      )}

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Futures Trading Card */}
          <div className="backdrop-blur rounded-lg p-6 border bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm font-semibold">Strategy</span>
              <Zap className="text-purple-400" size={20} />
            </div>
            <div className="text-xl font-bold mb-2 text-purple-300">
              ETH LONG-ONLY
            </div>
            <div className="text-xs text-gray-400">
              • 5x Leverage<br/>
              • Confidence ≥75 required<br/>
              • Pullback entry only
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Bot Status</span>
              <Activity className={isRunning ? 'text-green-400' : 'text-gray-500'} size={20} />
            </div>
            <div className="text-2xl font-bold">
              {isRunning ? (
                <span className="text-green-400">● Running</span>
              ) : (
                <span className="text-gray-400">○ Stopped</span>
              )}
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700" data-testid="eth-price-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">ETH Price</span>
              <TrendingUp className="text-blue-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-blue-400" data-testid="eth-price-value">
              ₹{toINR(ticker?.last || ticker?.price)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              ${ticker?.last?.toFixed(2) || ticker?.price?.toFixed(2) || '0.00'} USD
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700" data-testid="total-pl-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Total P/L</span>
              <DollarSign className="text-yellow-400" size={20} />
            </div>
            <div className={`text-2xl font-bold ${performance?.total_profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="total-pl-value">
              ₹{toINR(performance?.total_profit_loss)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              ${performance?.total_profit_loss?.toFixed(2) || '0.00'} USD
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Win Rate</span>
              <TrendingUp className="text-purple-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-purple-400">
              {performance?.win_rate?.toFixed(1) || '0.0'}%
            </div>
          </div>
        </div>

        {/* 🎯 QUICK TRADE STATUS - Main Decision Panel */}
        <QuickTradeStatus />

        {/* Confidence Score Panel */}
        {confidence?.available && (
          <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700 mb-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">📊 Live Confidence Score</h3>
              <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                confidence.can_enter 
                  ? 'bg-green-900/50 text-green-400 border border-green-600' 
                  : 'bg-red-900/50 text-red-400 border border-red-600'
              }`}>
                {confidence.can_enter ? '✅ READY TO ENTER' : '⏸️ WAITING'}
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Score Display */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className={`w-24 h-24 rounded-full border-4 flex items-center justify-center ${
                    confidence.score >= 75 ? 'border-green-500' : 
                    confidence.score >= 50 ? 'border-yellow-500' : 'border-red-500'
                  }`}>
                    <span className={`text-3xl font-bold ${
                      confidence.score >= 75 ? 'text-green-400' : 
                      confidence.score >= 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {confidence.score}
                    </span>
                  </div>
                  <div className="text-center mt-1 text-xs text-gray-400">out of 100</div>
                </div>
                <div className="text-sm">
                  <p className="text-gray-400 mb-1">Threshold: <span className="text-white font-bold">{confidence.threshold}</span></p>
                  <p className="text-gray-400">
                    {confidence.score >= confidence.threshold 
                      ? <span className="text-green-400">Score meets requirements!</span>
                      : <span className="text-yellow-400">{confidence.threshold - confidence.score} points needed</span>
                    }
                  </p>
                  {confidence.no_trade_reasons?.length > 0 && (
                    <div className="mt-2 text-xs text-red-400">
                      ⚠️ {confidence.no_trade_reasons.join(', ')}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Score Breakdown */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">4H Filter (ETH/BTC trend)</span>
                  <span className={`text-sm font-bold ${confidence.breakdown?.filter_4h >= 20 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {confidence.breakdown?.filter_4h}/30
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{width: `${(confidence.breakdown?.filter_4h / 30) * 100}%`}}></div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">1H Trend Strength</span>
                  <span className={`text-sm font-bold ${confidence.breakdown?.trend_1h >= 20 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {confidence.breakdown?.trend_1h}/30
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{width: `${(confidence.breakdown?.trend_1h / 30) * 100}%`}}></div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">Entry Timing</span>
                  <span className={`text-sm font-bold ${confidence.breakdown?.timing >= 15 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {confidence.breakdown?.timing}/25
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{width: `${(confidence.breakdown?.timing / 25) * 100}%`}}></div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">Risk Environment</span>
                  <span className={`text-sm font-bold ${confidence.breakdown?.risk >= 10 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {confidence.breakdown?.risk}/15
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{width: `${(confidence.breakdown?.risk / 15) * 100}%`}}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ETH LONG-Only Strategy Info */}
        <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="text-4xl">🎯</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-purple-300 mb-2">🎯 ETH LONG-Only Strategy - High Probability Entries</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-300">
                <div>
                  <p className="font-semibold text-white mb-1">📊 Confidence Scoring (0-100):</p>
                  <ul className="space-y-1 text-gray-400">
                    <li>• <span className="text-purple-400">4H Filter</span>: ETH/BTC trend (30 pts)</li>
                    <li>• <span className="text-purple-400">1H Trend</span>: EMAs, ADX, Volume (30 pts)</li>
                    <li>• <span className="text-purple-400">Entry Timing</span>: Pullback, RSI (25 pts)</li>
                    <li>• <span className="text-purple-400">Risk Environment</span>: BTC stability (15 pts)</li>
                    <li>• Entry only when <span className="text-green-400">score ≥ 75</span></li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-white mb-1">🛡️ Risk Management:</p>
                  <ul className="space-y-1 text-gray-400">
                    <li>• <span className="text-red-400">1% Stop Loss</span> (Hard protection)</li>
                    <li>• <span className="text-green-400">1.5% Take Profit</span> (Close 50%)</li>
                    <li>• <span className="text-purple-400">0.8% Trailing Stop</span> (After TP1)</li>
                    <li>• Max <span className="text-purple-400">3 trades/day</span></li>
                    <li>• Stop after <span className="text-purple-400">2 consecutive losses</span></li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg">
                <p className="text-sm text-yellow-300">
                  <strong>⚠️ Risk Warning:</strong> Futures trading involves 5x leverage. 
                  This bot only enters LONG positions when all conditions are met (score ≥75). 
                  Always start with paper trading. Never trade more than you can afford to lose.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Trading Charts with TradingView & AI Signals */}
        <AdvancedTradingChart />
        
        {/* Legacy Trading Charts */}
        <TradingChart trades={trades} ticker={ticker} />

        <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6 border border-gray-700 mt-6">
          <h2 className="text-xl font-bold mb-4 text-purple-400">📊 Recent Futures Trades</h2>
          
          {trades.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No trades yet. Start the bot to begin futures trading!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="trades-table">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 px-4 text-gray-400">Time</th>
                    <th className="text-left py-2 px-4 text-gray-400">Side</th>
                    <th className="text-left py-2 px-4 text-gray-400">Entry (INR)</th>
                    <th className="text-left py-2 px-4 text-gray-400">Exit (INR)</th>
                    <th className="text-left py-2 px-4 text-gray-400">P/L (INR)</th>
                    <th className="text-left py-2 px-4 text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((trade, idx) => (
                    <tr key={idx} className="border-b border-gray-800" data-testid={`trade-row-${idx}`}>
                      <td className="py-3 px-4 text-gray-300">
                        {new Date(trade.entry_time).toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          trade.side === 'buy' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'
                        }`}>
                          {trade.side?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4">₹{toINR(trade.entry_price)}</td>
                      <td className="py-3 px-4">
                        {trade.exit_price ? `₹${toINR(trade.exit_price)}` : '-'}
                      </td>
                      <td className="py-3 px-4">
                        {trade.profit_loss ? (
                          <span className={trade.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}>
                            ₹{toINR(trade.profit_loss)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          trade.status === 'open' ? 'bg-blue-900 text-blue-400' : 'bg-gray-700 text-gray-400'
                        }`}>
                          {trade.status?.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingDashboard;
